import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { useEntityFileAttachments, useDeleteFileAttachment } from '../../hooks/useFileAttachments';
import { UploadIcon, FileIcon, XIcon, CheckIcon, SpinnerIcon, DownloadIcon } from './Icons';
import { normalizeFileName } from './utils/fileNameEncoding';

/**
 * FormFileUpload_2 - Enhanced responsive file upload component
 * Features:
 * - Fully responsive design (mobile, tablet, desktop)
 * - Card-based layout with adaptive grid
 * - Drag & drop with visual feedback
 * - File preview with thumbnails for images
 * - Progress indicators
 * - Touch-friendly for mobile devices
 * - Better visual hierarchy
 */
const FormFileUpload_2 = React.forwardRef(({ 
  name, 
  label, 
  value = [], 
  onChange, 
  entityTypeId, 
  entityId, 
  multiple = true,
  accept,
  maxSize,
  maxFiles,
  placeholder = "Drop files here or click to browse",
  required = false,
  disabled = false,
  className = '',
  onFilesToDeleteChange,
  // Additional props for compatibility
  error = null,
  showPreview = true,
  readOnly = false,
  userId = null,
  description = '',
  isPrivate = false,
  onFileUpload = null,
  onFileRemove = null,
  onError = null
}, ref) => {
  const { theme } = useTheme();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  
  // Fetch existing files for this entity
  const { data: existingFiles = [], isLoading: loadingExistingFiles, mutate: mutateExistingFiles } = useEntityFileAttachments(entityTypeId, entityId);
  
  // Delete file attachment hook
  const { deleteFileAttachment, isDeleting } = useDeleteFileAttachment();

  // Clear filesToDelete when entityId changes
  useEffect(() => {
    if (entityId) {
      setFilesToDelete([]);
    }
  }, [entityId]);

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName, fileType) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    
    if (fileType?.startsWith('image/')) {
      return '🖼️';
    } else if (fileType?.includes('pdf')) {
      return '📄';
    } else if (fileType?.includes('word') || extension === 'doc' || extension === 'docx') {
      return '📝';
    } else if (fileType?.includes('excel') || extension === 'xls' || extension === 'xlsx') {
      return '📊';
    } else if (fileType?.includes('zip') || extension === 'zip' || extension === 'rar') {
      return '📦';
    }
    return '📎';
  };

  const handleFiles = useCallback((eventOrFiles) => {
    let files = [];
    
    // Handle both direct FileList and event objects
    if (eventOrFiles.target && eventOrFiles.target.files) {
      files = Array.from(eventOrFiles.target.files);
    } else if (eventOrFiles instanceof FileList) {
      files = Array.from(eventOrFiles);
    } else if (Array.isArray(eventOrFiles)) {
      files = eventOrFiles;
    }

    // Validate files
    if (maxFiles && files.length > maxFiles) {
      console.warn(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const oversizedFiles = files.filter(file => maxSize && file.size > maxSize);
    if (oversizedFiles.length > 0) {
      console.warn(`File(s) too large. Maximum size is ${formatFileSize(maxSize)}`);
      return;
    }

    // Create file attachment objects
    const fileAttachments = files.map(file => ({
      file: file,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      entityTypeId: entityTypeId,
      entityId: entityId ? Number(entityId) : null,
      description: '',
      isPrivate: false,
      uploadedAt: new Date().toISOString()
    }));

    if (onChange) {
      onChange({
        target: {
          name,
          value: fileAttachments,
          fileAttachments: fileAttachments
        }
      });
    }
  }, [name, onChange, entityTypeId, entityId, maxFiles, maxSize]);

  // Filter files for display
  const newFilesOnly = Array.isArray(value) ? value.filter(newFile => 
    !existingFiles.some(existingFile => 
      existingFile.id === newFile.id || 
      existingFile.fileName === newFile.fileName
    )
  ) : [];
  
  const filteredExistingFiles = existingFiles.filter(file => 
    !file.isDeleted && 
    !filesToDelete.some(fileToDelete => fileToDelete.id === file.id)
  );
  
  const displayFiles = filteredExistingFiles.map(file => ({
    ...file,
    isExisting: true
  })).concat(newFilesOnly.map(file => ({
    ...file,
    isExisting: false
  })));

  const handleRemoveFile = useCallback((index) => {
    const fileToRemove = displayFiles[index];
    
    if (fileToRemove.isExisting) {
      const newFilesToDelete = [...filesToDelete, fileToRemove];
      setFilesToDelete(newFilesToDelete);
      if (onFilesToDeleteChange) {
        onFilesToDeleteChange(newFilesToDelete);
      }
    } else {
      const existingFilesCount = filteredExistingFiles.length;
      const actualIndex = index - existingFilesCount;
      
      if (actualIndex >= 0 && actualIndex < newFilesOnly.length) {
        const fileToRemoveFromNew = newFilesOnly[actualIndex];
        const newFiles = Array.isArray(value) ? value.filter((file) => {
          if (file.file && fileToRemoveFromNew.file) {
            return file.file !== fileToRemoveFromNew.file;
          }
          const fileLastModified = file.file?.lastModified || file.lastModified || 0;
          const removeLastModified = fileToRemoveFromNew.file?.lastModified || fileToRemoveFromNew.lastModified || 0;
          if (file.fileName === fileToRemoveFromNew.fileName &&
              file.fileSize === fileToRemoveFromNew.fileSize &&
              fileLastModified === removeLastModified) {
            return false;
          }
          return true;
        }) : [];
        
        if (onChange) {
          onChange({
            target: {
              name,
              value: newFiles,
              fileAttachments: newFiles
            }
          });
        }
      }
    }
  }, [name, onChange, value, displayFiles, filteredExistingFiles.length, newFilesOnly, filesToDelete, onFilesToDeleteChange]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(false);
    }
  }, [disabled]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  }, [disabled, handleFiles]);

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFiles(files);
    }
    // Reset input to allow selecting the same file again
    e.target.value = '';
  };

  // Expose methods via ref
  React.useImperativeHandle(ref, () => ({
    getFilesToDelete: () => filesToDelete
  }), [filesToDelete]);

  return (
    <div className={cn("space-y-4", className)}>
      {label && (
        <label className={cn('block text-sm font-semibold mb-2', theme.text.primary)}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
          {error}
        </div>
      )}
      
      {/* Drag & Drop Zone */}
      <div
        ref={dropZoneRef}
        className={cn(
          'relative w-full rounded-xl border-2 border-dashed transition-all duration-300',
          theme.colors.surface,
          'hover:border-blue-400 hover:bg-blue-50/40',
          isDragOver
            ? 'border-blue-500 bg-blue-50 scale-[1.01]'
            : theme.border.light,
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && 'cursor-pointer',
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />
        
        <div className="flex flex-col items-center justify-center p-6 sm:p-8 md:p-12 text-center min-h-[140px] sm:min-h-[160px]">
          <div className={cn(
            "mb-4 transition-transform duration-300",
            isDragOver && "scale-110"
          )}>
            <UploadIcon className={cn('w-10 h-10 sm:w-12 sm:h-12', theme.text.muted)} />
          </div>
          
          <p className={cn('text-sm sm:text-base font-medium mb-1', theme.text.primary)}>
            {placeholder}
          </p>
          
          <p className={cn('text-xs sm:text-sm mt-2', theme.text.muted)}>
            {accept && (
              <span className="block sm:inline">Accepted: {accept}</span>
            )}
            {accept && maxSize && <span className="hidden sm:inline"> • </span>}
            {maxSize && (
              <span className="block sm:inline">Max size: {formatFileSize(maxSize)}</span>
            )}
            {multiple && maxFiles && (
              <>
                <span className="hidden sm:inline"> • </span>
                <span className="block sm:inline">Max files: {maxFiles}</span>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Loading State */}
      {Boolean(entityId) && loadingExistingFiles && (
        <div className={cn('flex items-center gap-2 text-sm', theme.text.muted)}>
          <SpinnerIcon className="w-4 h-4 animate-spin" />
          <span>Loading existing files...</span>
        </div>
      )}

      {/* Files List */}
      {displayFiles.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className={cn('text-sm font-semibold', theme.text.primary)}>
              {multiple ? `Files (${displayFiles.length})` : 'File'}
            </h4>
            {displayFiles.length > 0 && (
              <span className={cn('text-xs', theme.text.muted)}>
                {filteredExistingFiles.length} existing, {newFilesOnly.length} new
              </span>
            )}
          </div>
          
          {/* Compact List/Row Layout */}
          <div className="space-y-1.5">
            {displayFiles.map((file, index) => {
              const fileName = normalizeFileName(file.fileName || file.name || '');
              const fileSize = file.fileSize || file.size;
              const fileType = file.fileType || file.type;
              const isExisting = file.isExisting;
              const fileId = file.id;
              
              // Check if file is an image for thumbnail
              const isImage = fileType?.startsWith('image/');
              const imageUrl = isImage && file.file ? URL.createObjectURL(file.file) : null;
              
              return (
                <div
                  key={index}
                  className={cn(
                    "group relative flex items-center gap-2 p-2 rounded-md border transition-all duration-200",
                    "hover:bg-gray-50 dark:hover:bg-gray-800/50",
                    theme.colors.background,
                    theme.border.subtle,
                    isExisting && "border-l-2 border-l-blue-500 bg-blue-50/20 dark:bg-blue-900/10"
                  )}
                >
                  {/* File Icon/Thumbnail - Smaller */}
                  <div className="flex-shrink-0 relative">
                    {isImage && imageUrl ? (
                      <div
                        className={cn(
                          'w-8 h-8 rounded overflow-hidden border',
                          theme.colors.background,
                          theme.border.light,
                        )}
                      >
                        <img
                          src={imageUrl}
                          alt={fileName}
                          className="w-full h-full object-cover"
                          onLoad={() => imageUrl && URL.revokeObjectURL(imageUrl)}
                        />
                      </div>
                    ) : (
                      <div
                        className={cn(
                          'w-8 h-8 rounded border flex items-center justify-center',
                          theme.colors.background,
                          theme.border.light,
                        )}
                      >
                        <span className="text-lg">{getFileIcon(fileName, fileType)}</span>
                      </div>
                    )}
                    
                    {/* Existing Badge - Tiny indicator */}
                    {isExisting && (
                      <div
                        className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-blue-500 rounded-full border border-white"
                        title="Existing file"
                      />
                    )}
                  </div>
                  
                  {/* File Info - Compact layout */}
                  <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs sm:text-sm">
                        <p className="font-medium text-gray-900 dark:text-gray-100 truncate" title={fileName}>
                          {fileName}
                        </p>
                        <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {formatFileSize(fileSize)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Actions - Compact */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {isExisting && fileId && (
                        <a
                          href={API_ENDPOINTS.fileAttachment.download(fileId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="w-6 h-6 rounded flex items-center justify-center text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Download file"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(index);
                        }}
                        className={cn(
                          "w-6 h-6 rounded flex items-center justify-center transition-all duration-200",
                          "bg-red-500 text-white hover:bg-red-600 active:scale-95",
                          disabled && "opacity-50 cursor-not-allowed"
                        )}
                        title={isExisting ? "Mark for deletion" : "Remove file"}
                        disabled={disabled}
                      >
                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload/Delete Status */}
      {(isUploading || isDeleting) && (
        <div className={cn('flex items-center gap-2 text-sm text-blue-600')}>
          <SpinnerIcon className="w-4 h-4 animate-spin" />
          <span>{isUploading ? 'Uploading files...' : 'Deleting file...'}</span>
        </div>
      )}
    </div>
  );
});

FormFileUpload_2.displayName = "FormFileUpload_2";

export default FormFileUpload_2;

